import {
  Block,
  BlockRelationship,
  BlockWithChildren,
  CommonBlocksCompoundIndexes,
  RootBlock,
  blockRelationshipBaseOps,
  getBRId,
  isBlockRelationship,
  isBlockWithChildren,
  isBranch,
  isRootBlock,
  isVirtualInstanceChildTarget,
  makeInstanceChildBlock,
  normalizeStringPath,
} from '@basehub/data/replicache/index'
import { WriteTransaction } from 'replicache'
import { invariant } from '@epic-web/invariant'
import { getIdempotentId } from '@basehub/utils/id/index'
import { TreeManager } from '@basehub/utils/tree/index'
import { AtLeast } from './crud'
import { blockBaseOps, ensureBaseHubTransaction } from '../../../../tx/iso'
import {
  getAgentBlockCoWId,
  makeAgentBlockCopy,
} from '@basehub/data/replicache/schema/block/agents'
import { PerfReport } from '@basehub/utils/perf/index'
import { tryCatch } from '@basehub/utils'

type PartialParentBlock = { id: string; type: 'instance' }

/**
 * Perform the copy-on-write operation.
 * Used when editing a committed block inside a branch's working tree.
 * And/or when we're updating a virtualized instance child.
 */
export const doCopyOnWrite = async (
  tx: WriteTransaction,
  {
    partialBlock,
    path,
    isoNow,
    debug,
    perfReport,
  }: {
    partialBlock: AtLeast<Block, 'id' | 'instanceChildTarget'>
    path: string
    isoNow: string
    debug?: boolean
    perfReport?: PerfReport
  }
): Promise<{
  block: Block
  blockRelationship: BlockRelationship
  parentBlock: BlockWithChildren | PartialParentBlock
  rootBlock: RootBlock
  didCopy: boolean
  path: string
}> => {
  ensureBaseHubTransaction(tx)

  const playgroundId =
    tx.clientOnly?.playgroundId ?? tx.serverOnly?.playgroundId

  let normalizedPath = normalizeStringPath({
    path,
    edgeId: partialBlock.id,
    format: 'array',
    includeEdge: true,
  })

  let rootBlockId = normalizedPath[0]
  invariant(rootBlockId, 'rootBlockId not found')

  if (playgroundId && tx.serverOnly) {
    const playgroundBranch = tx.serverOnly.resolvedRef?.value
    if (
      playgroundBranch &&
      isBranch(playgroundBranch) &&
      playgroundBranch.workingRootBlockId &&
      playgroundBranch.playgroundId === playgroundId
    ) {
      path = path.replace(rootBlockId, playgroundBranch.workingRootBlockId)
      normalizedPath = normalizeStringPath({
        path,
        edgeId: partialBlock.id,
        format: 'array',
        includeEdge: true,
      })
      rootBlockId = playgroundBranch.workingRootBlockId
    } else {
      throw new Error(
        `Attempting to copy-on-write on a root that's not in a playground branch.`
      )
    }
  }

  const _parentId = normalizedPath[normalizedPath.length - 2]
  invariant(_parentId, '_parentId not found')

  let block
  let blockRelationship
  let didCopy = false

  const tm = tx.treeManagerCache.get(rootBlockId)
  // const brs = tx.serverOnly && tm?.root.value ? tx.getBrsByRootId(tm.root.value as RootBlock) : undefined

  const segment = normalizedPath[normalizedPath.length - 1]
  const parentSegment = normalizedPath[normalizedPath.length - 2] ?? null
  let shouldSetBlock = false
  let shouldSetBr = false
  let isSegmentBlockVirtualized = false
  invariant(segment, 'segment not found')
  let [rootBlock, parentBlock, segmentBlock, segmentBr, instanceChildTarget] =
    await Promise.all([
      tm?.getNode(rootBlockId)?.value ?? blockBaseOps.get(tx, rootBlockId),
      tm?.getNode(_parentId)?.value ?? blockBaseOps.get(tx, _parentId),
      tm?.getNode(partialBlock.id)?.value ??
        blockBaseOps.get(tx, partialBlock.id),
      tx.getBlockRelationshipByBlockId({
        blockId: partialBlock.id,
        rootBlockId,
        includeDiscarded: true,
      }),
      partialBlock.instanceChildTarget
        ? tx
            .getBlockRelationshipByBlockId({
              blockId: partialBlock.instanceChildTarget,
              rootBlockId,
            })
            .then(async (br) => {
              invariant(br, 'br not found')
              const block = await blockBaseOps.get(tx, br.blockId)
              invariant(block, 'block not found')
              return { br, block }
            })
        : undefined,
    ])
  invariant(rootBlock, 'rootBlock not found')
  invariant(isRootBlock(rootBlock), 'rootBlock is not a root block')
  // parentBlock can be undefined/null because it can be also virtualized (nested instances)
  // in that case, to virtualize the instance child, we can use `parentId` assuming it's a sourceBlockId / deterministic as it comes from a virtual parent

  if (
    segmentBlock &&
    segmentBr &&
    isVirtualInstanceChildTarget(segmentBlock) &&
    segmentBlock.type === 'agent'
  ) {
    // this surely is a built-in Agent; this logic is handled by the makeAgentBlockCopy function (special case),
    // so we'll null this and let that code path get called.
    segmentBlock = undefined
    segmentBr = null
  }

  if (segmentBlock) {
    partialBlock.id = segmentBlock.id
  }

  perfReport?.checkpoint('doCopyOnWrite:initial-data')

  if (debug) {
    // eslint-disable-next-line no-console
    console.log({
      segmentBlock,
      parentBlock,
      segmentBr,
      instanceChildTarget,
      partialBlock,
      path,
    })
  }

  if (
    segmentBlock &&
    !segmentBlock.isCommitted &&
    !isVirtualInstanceChildTarget(segmentBlock) &&
    !!playgroundId
  ) {
    invariant(
      segmentBlock.playgroundId === playgroundId,
      `This block (${segmentBlock.id}) belongs to a playground, so it cannot be used for copy-on-write operations in regular branches.`
    )
    invariant(
      playgroundId === rootBlock.playgroundId,
      `The root block (${rootBlock.id}) belongs to a playground, so it cannot be used for copy-on-write operations in regular branches.`
    )
  }

  let newBlockId = ''
  let virtualBlockIdToCleanup = ''

  if (segmentBlock && '__virtual' in segmentBlock) {
    segmentBlock = { ...segmentBlock }
    // @ts-expect-error
    delete segmentBlock.__virtual
    // @ts-expect-error
    delete segmentBlock.__virtualInstanceChildTarget
    shouldSetBlock = true
    isSegmentBlockVirtualized = true
    virtualBlockIdToCleanup = segmentBlock.id
    newBlockId =
      segmentBlock.type === 'agent'
        ? getAgentBlockCoWId({
            rootBlock,
            targetSourceBlockId: segmentBlock.sourceBlockId || segmentBlock.id,
          })
        : getCoRBlockKey({
            rootBlockId,
            sourceBlockId: idToSourceBlockId(segmentBlock),
          })
    segmentBlock.id = newBlockId
  }

  if (segmentBr && '__virtual' in segmentBr) {
    segmentBr = { ...segmentBr }
    // @ts-expect-error
    delete segmentBr.__virtual
    shouldSetBr = true
  }

  const sourceBlockId =
    segmentBlock?.sourceBlockId ??
    segmentBr?.sourceBlockId ??
    segmentBlock?.id ??
    segment

  if (!newBlockId) {
    newBlockId = getCoRBlockKey({ rootBlockId, sourceBlockId })
  }

  if (!segmentBlock || !segmentBr) {
    const [existingCopy, existingCopyBr] = await Promise.all([
      tm?.getNode(newBlockId)?.value ?? blockBaseOps.get(tx, newBlockId),
      tx.getBlockRelationshipByBlockId({
        blockId: newBlockId,
        rootBlockId,
        includeDiscarded: true,
      }),
    ])
    if (!segmentBlock && existingCopy) {
      segmentBlock = structuredClone(existingCopy)
      delete segmentBlock.deletedAt
      partialBlock = { ...partialBlock, id: existingCopy.id }
      shouldSetBlock = true
    }
    if (!segmentBr && existingCopyBr) {
      segmentBr = structuredClone(existingCopyBr)
      delete segmentBr.deletedAt
      delete segmentBr.discardedAt
      shouldSetBr = true
    }
    perfReport?.checkpoint('doCopyOnWrite:existing-copy-checks')
  }

  if (!segmentBlock || !segmentBr) {
    if (!instanceChildTarget) {
      const builtInAgentCopy = makeAgentBlockCopy({
        agentId: segment,
        rootBlock,
        isoNow,
      })

      // if the segment is a system agent, we can virtualize it without instanceChildTarget
      if (!builtInAgentCopy.block) {
        console.log({
          builtInAgentCopy,
          segment,
          segmentBlock,
          segmentBr,
          parentBlock,
          rootBlock,
        })
        throw new Error(
          `[copy-on-write] segmentBlock was not found and instanceChildTarget wasn't either, so virtualization is not possible. failing.`
        )
      }

      isSegmentBlockVirtualized = true
      segmentBlock = builtInAgentCopy.block
      newBlockId = segmentBlock.id
      segmentBr = builtInAgentCopy.br
    } else {
      invariant(
        instanceChildTarget,
        `segmentBlock was not found and instanceChildTarget wasn't either, so virtualization is not possible. failing.`
      )
      isSegmentBlockVirtualized = true
      const parentId = parentBlock?.sourceBlockId || _parentId
      const enhanced = makeInstanceChildBlock({
        block: null,
        targetBlock: instanceChildTarget.block,
        parentSourceBlockId: parentId,
      })
      delete enhanced.__virtual
      // @ts-expect-error
      delete enhanced.__virtualInstanceChildTarget
      enhanced.id = newBlockId
      segmentBlock = enhanced
      segmentBr = {
        branchId: rootBlock.branchId ?? instanceChildTarget.br.branchId,
        validFrom: instanceChildTarget.br.validFrom,
        validTo: instanceChildTarget.br.validTo,
        blockId: newBlockId,
        id: getBRId(segmentBlock.id, rootBlockId),
        isCommitted: false,
        parentId,
        sourceBlockId: idToSourceBlockId(segmentBlock),
        order: instanceChildTarget.br.order,
        hash: '',
        createdAt: isoNow,
        structural: false, // cause virtual
        ...(playgroundId ? { playgroundId } : {}),
      } as BlockRelationship
    }
  }

  invariant(segmentBlock, `block not found for id: ${segment}`)
  if (!segmentBr) {
    // eslint-disable-next-line no-console
    console.log({ segment, segmentBlock, segmentBr, rootBlockId })
  }
  invariant(
    segmentBr,
    `segmentBr not found for id: ${segment} and rootBlockId: ${rootBlockId}`
  )
  if (segmentBr.discardedAt) {
    segmentBr = { ...segmentBr }
    delete segmentBr.discardedAt
    shouldSetBr = true
  }
  // merge it to preserve partialBlock updates (if any)
  segmentBlock = {
    ...segmentBlock,
    ...(partialBlock as typeof segmentBlock),
    ...(playgroundId ? { playgroundId } : {}),
  } as typeof segmentBlock

  block = segmentBlock
  blockRelationship = segmentBr

  const shouldCopyBlock =
    block.isCommitted ||
    isSegmentBlockVirtualized ||
    (!block.isCommitted && blockRelationship.branchId !== rootBlock.branchId)
  const shouldCopyBr =
    blockRelationship.isCommitted ||
    isSegmentBlockVirtualized ||
    (!blockRelationship.isCommitted &&
      blockRelationship.branchId !== rootBlock.branchId)

  if (shouldCopyBlock || shouldCopyBr) {
    const existingCopyID = tx.copyOnWriteCache.get(
      `${rootBlockId}:${newBlockId}`
    )
    const existingCopyBrID = tx.copyOnWriteCache.get(
      `${rootBlockId}:${getBRId(newBlockId, rootBlockId)}`
    )

    const [existingCopy, existingCopyBr] = await Promise.all([
      existingCopyID
        ? (tm?.getNode(existingCopyID)?.value ??
          blockBaseOps.get(tx, existingCopyID).then((b) => {
            perfReport?.checkpoint('doCopyOnWrite:existing-BLOCK-check')
            return b
          }))
        : undefined,
      existingCopyBrID
        ? blockRelationshipBaseOps.get(tx, existingCopyBrID).then((br) => {
            perfReport?.checkpoint('doCopyOnWrite:existing-BR-check')
            return br
          })
        : undefined,
    ])

    if (shouldCopyBlock) {
      if (existingCopy) {
        if (existingCopy.isCommitted) {
          throw new Error(
            `block with id ${newBlockId} already exists and is committed when it shouldn't be`
          )
        }
        block = existingCopy
      } else {
        const copy: Block = mergeBlockWithCopyOnWriteProps(segmentBlock, {
          isCommitted: false,
          updatedAt: isoNow,
          id: newBlockId,
        })
        block = copy
      }
    }

    if (shouldCopyBr) {
      if (existingCopyBr) {
        if (existingCopyBr.isCommitted) {
          throw new Error(
            `BR with id ${existingCopyBr.id} already exists and is committed when it shouldn't be`
          )
        }
        blockRelationship = existingCopyBr
        blockRelationship.blockId = block.id
        shouldSetBr = true
      } else {
        const newBrValidFrom = rootBlock.createdAt
        const newBr: BlockRelationship = {
          ...blockRelationship,
          isCommitted: false,
          id: getBRId(block.id, rootBlockId),
          blockId: block.id,
          branchId: rootBlock.branchId,
          validFrom: newBrValidFrom,
          ...(rootBlock.isInlineSuggestion ? { isInlineSuggestion: true } : {}),
          ...(playgroundId ? { playgroundId } : {}),
        }
        delete newBr.validTo
        shouldSetBr = true
        blockRelationship = newBr
      }
    } else if (block.id !== blockRelationship.blockId) {
      blockRelationship = structuredClone(blockRelationship)
      blockRelationship.blockId = block.id
      shouldSetBr = true
    }

    didCopy = true
    /** Then add the new node (this will add it to the treeManager if it exists) */
    block.updatedAt = isoNow
    shouldSetBlock = true

    tx.copyOnWriteCache.set(`${rootBlockId}:${segmentBlock.id}`, block.id)
    tx.copyOnWriteCache.set(
      `${rootBlockId}:${segmentBr.id}`,
      blockRelationship.id
    )
    perfReport?.checkpoint('doCopyOnWrite:did-copy')
  }

  invariant(block, 'block not found')
  invariant(blockRelationship, 'blockRelationship not found')
  invariant(rootBlock, 'rootBlock not found')
  invariant(isRootBlock(rootBlock), 'rootBlock is not a root block')

  let partialParentBlock: PartialParentBlock | undefined = undefined
  if (!parentBlock) {
    partialParentBlock = {
      id: _parentId,
      type: 'instance',
    }
  }

  invariant(
    parentBlock ? isBlockWithChildren(parentBlock) : partialParentBlock,
    'parentBlock not found'
  )

  if (blockRelationship.blockId !== block.id) {
    // eslint-disable-next-line no-console
    console.error({
      blockRelationship,
      block,
      partialBlock,
      segmentBlock,
      segmentBr,
      didCopy,
      shouldCopyBlock,
      shouldCopyBr,
      shouldSetBlock,
      shouldSetBr,
      isSegmentBlockVirtualized,
    })
  }
  invariant(
    blockRelationship.blockId === block.id,
    'blockRelationship.blockId !== block.id'
  )

  if (debug) {
    // eslint-disable-next-line no-console
    console.log('RESULT', { didCopy, block, blockRelationship })
  }

  if (tm && didCopy) {
    /** Delete the old node from the treeManager (if it exists) */
    const existingNode =
      tm.getNode(segment) || tm.getNode(idToSourceBlockId(block))
    if (existingNode) {
      const prevChildrenKeys =
        tryCatch(() => tm.getChildrenKeys(segment)).result ?? []
      if (virtualBlockIdToCleanup) {
        tm.deleteNode(virtualBlockIdToCleanup)
      }
      tm.deleteNode(segment)
      tm.upsertNode(block, {
        parentKey: parentBlock?.id ?? partialParentBlock?.id ?? null,
        order: existingNode.order,
      })
      /** Then re-parent child nodes */
      for (const child of prevChildrenKeys) {
        if (!tm.getNode(child)) continue
        tm.updateNode(child, { parentKey: block.id })
      }
    }
  }

  if (shouldSetBlock) {
    await blockBaseOps.put(tx, block, parentSegment)
  }
  if (shouldSetBr) {
    await blockRelationshipBaseOps.set(tx, blockRelationship)
  }

  perfReport?.checkpoint('doCopyOnWrite:done')

  return {
    block,
    blockRelationship,
    parentBlock:
      parentBlock && isBlockWithChildren(parentBlock)
        ? parentBlock
        : partialParentBlock!,
    rootBlock,
    path: [
      ...normalizeStringPath({
        path: normalizedPath.join('/'),
        edgeId: partialBlock.id,
        includeEdge: false,
        format: 'array',
      }),
      block.id,
    ].join('/'),
    didCopy,
  }
}

export const simpleCopyOnWrite = <T extends Block | BlockRelationship>({
  brOrBlock,
  extras,
  rootBlock,
  isoNow,
  idSeed,
}: {
  brOrBlock: T
  extras?: Partial<T>
  rootBlock: RootBlock
  isoNow: string
  idSeed: string
}): T => {
  if (isBlockRelationship(brOrBlock)) {
    const newBr = {
      ...brOrBlock,
      branchId: rootBlock.branchId,
      validFrom: rootBlock.createdAt,
      id: getBRId(brOrBlock.blockId, rootBlock.id),
      isCommitted: false,
      ...(extras as undefined | Partial<BlockRelationship>),
    } satisfies BlockRelationship
    delete newBr.validTo
    return newBr as T
  } else {
    const newBlock = mergeBlockWithCopyOnWriteProps(brOrBlock, {
      updatedAt: isoNow,
      id: getIdempotentId({ idSeed: idSeed + brOrBlock.id }),
      isCommitted: false,
      ...(extras as undefined | Partial<Block>),
    })
    return newBlock as T
  }
}

export function getCoRBlockKey({
  rootBlockId,
  sourceBlockId,
}: {
  rootBlockId: string
  sourceBlockId: string
}) {
  return getIdempotentId({ idSeed: rootBlockId + sourceBlockId })
}

export function idToSourceBlockId(
  block: AtLeast<Block, 'id' | 'sourceBlockId'>
): string
export function idToSourceBlockId(
  id: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  treeManager: AtLeast<TreeManager<Block, any>, 'getNode'>
): string
export function idToSourceBlockId(
  idOrBlock: string | AtLeast<Block, 'id' | 'sourceBlockId'>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  treeManager?: AtLeast<TreeManager<Block, any>, 'getNode'>
): string {
  if (typeof idOrBlock === 'string') {
    invariant(treeManager, 'treeManager is required for this overload')
    const node = treeManager.getNode(idOrBlock, {
      fallbackKey: 'sourceBlockId',
    })
    return node?.value.sourceBlockId || idOrBlock
  } else {
    return idOrBlock.sourceBlockId || idOrBlock.id
  }
}

export function sourceBlockIdToId(block: Block): string
export function sourceBlockIdToId(
  sourceBlockId: string,
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes>
): string
export function sourceBlockIdToId(
  sourceBlockIdOrBlock: string | Block,
  treeManager?: TreeManager<Block, CommonBlocksCompoundIndexes>
): string {
  if (typeof sourceBlockIdOrBlock === 'string') {
    invariant(treeManager, 'treeManager is required for this overload')
    const node = treeManager.getNode(sourceBlockIdOrBlock, {
      fallbackKey: 'sourceBlockId',
      required: true,
    })
    return node.value.id || sourceBlockIdOrBlock
  } else {
    return sourceBlockIdOrBlock.id || sourceBlockIdOrBlock.sourceBlockId || ''
  }
}

export const mergeBlockWithCopyOnWriteProps = (
  block: Block,
  props: Pick<Block, 'id' | 'isCommitted' | 'updatedAt'>
) => {
  return {
    ...block,
    sourceBlockId: idToSourceBlockId(block),
    id: props.id,
    isCommitted: props.isCommitted,
    updatedAt: props.updatedAt,
  }
}
